import React from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';

const AXIS_COLOR = '#7a8499';
// Chart series colours (identity, not magnitude): the game in brand blue,
// the normal-release benchmark in neutral grey, its peer launches in yellow.
export const SERIES = {
  game:   { color: '#3861FB', label: 'This game' },
  normal: { color: '#7a8499', label: 'Normal releases' },
  band:   { color: '#7a8499', label: 'Normal releases (middle 50%)' },
  pre:    { color: '#FFD22F', label: 'Other pre-releases (average)' },
};

function LegendItem({ color, label, dashed, band }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-ink-dim">
      {band ? (
        <span className="h-2.5 w-4 rounded-sm" style={{ backgroundColor: color, opacity: 0.3 }} />
      ) : (
        <span className="w-4 border-t-2" style={{ borderColor: color, borderStyle: dashed ? 'dashed' : 'solid' }} />
      )}
      {label}
    </span>
  );
}

function CurveTooltip({ active, payload, fmt, peerLabel, normalLabel }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const rows = [
    [SERIES.game, p.game],
    [{ ...SERIES.normal, label: normalLabel }, p.normal_avg],
    [{ ...SERIES.pre, label: peerLabel }, p.pre_avg],
  ];
  return (
    <div className="bg-surface-2 border border-white/10 rounded-md p-2.5 text-[11px] min-w-[220px]">
      <p className="font-bold text-ink mb-1">First {p.day} day{p.day === 1 ? '' : 's'}</p>
      {rows.map(([s, v]) => (
        <div key={s.label} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
          <span className="text-ink-dim">{s.label}</span>
          <span className="text-ink ml-auto pl-2">{v == null ? '—' : fmt(v)}</span>
        </div>
      ))}
      {p.band && (
        <p className="text-ink-dim mt-1">Middle 50%: {fmt(p.band[0])} – {fmt(p.band[1])} ({p.normal_n} games)</p>
      )}
    </div>
  );
}

const MARKER_COLOR = '#c7cdd9';

// Cumulative launch curve of one game against the normal-release band and
// average and its peer launches' average, by days since release. `markers`
// are vertical lines for the game's phase changes (VIP week end, general
// release) on the same day axis.
export default function LaunchCurveChart({
  data, fmt, axisFmt, peerLabel = SERIES.pre.label, markers = [], height = 320,
}) {
  const normalLabel = `${SERIES.normal.label} (average)`;
  return (
    <div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
        <LegendItem color={SERIES.game.color} label={SERIES.game.label} />
        <LegendItem color={SERIES.normal.color} label={normalLabel} dashed />
        <LegendItem color={SERIES.band.color} label={SERIES.band.label} band />
        <LegendItem color={SERIES.pre.color} label={peerLabel} dashed />
      </div>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="day" stroke={AXIS_COLOR} fontSize={11} tickFormatter={d => `D${d}`} />
            <YAxis tickFormatter={axisFmt} stroke={AXIS_COLOR} fontSize={11} width={56} />
            <Tooltip content={<CurveTooltip fmt={fmt} peerLabel={peerLabel} normalLabel={normalLabel} />} cursor={{ stroke: 'rgba(255,255,255,0.15)' }} />
            <Area dataKey="band" stroke="none" fill={SERIES.band.color} fillOpacity={0.2} isAnimationActive={false} connectNulls />
            <Line dataKey="normal_avg" stroke={SERIES.normal.color} strokeDasharray="5 4" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line dataKey="pre_avg" stroke={SERIES.pre.color} strokeDasharray="5 4" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
            <Line dataKey="game" stroke={SERIES.game.color} strokeWidth={2.5} dot={{ r: 2 }} isAnimationActive={false} />
            {markers.map(m => (
              <ReferenceLine
                key={m.key}
                x={m.day}
                stroke={MARKER_COLOR}
                strokeDasharray="2 3"
                label={{ value: m.label, position: 'insideTopLeft', fill: MARKER_COLOR, fontSize: 10 }}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
